import React from 'react';
import PropTypes from 'prop-types';
import polyline from '@mapbox/polyline';
import simplify from "@turf/simplify";
import imageOpenInNew from '../../../components/pages/dashboard/EventMedia/images/open-in-new-w64.svg';
import { mapboxToken } from '../mapboxHelper';
import MapboxControl from './MapboxControl';
import EventMediaPreloaderUnderlay from '../../../components/pages/dashboard/EventMedia/EventMediaPreloaderUnderlay';
import lsUtil from '../../common/lsUtil';
import { strings } from '../../../localization';
//import { requestEventMediaFullscreen } from '../../../components/pages/dashboard/EventMedia/EventMedia';

const SIMPLIFY_TOLERANCES =[0.0005, 0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5, 1];

function simplifyPoints(gpsPointsLatLon, tolerance = SIMPLIFY_TOLERANCES[0]) { // expects [[lat, lon]]
    if (!gpsPointsLatLon || gpsPointsLatLon.length === 0) {
        return [];
    }

    // Convert to [lng, lat] for Turf.js
    const gpsPointsLngLat = gpsPointsLatLon.map(p => [p[1], p[0]]);
    
    const geojsonLine = {
        type: 'Feature',
        geometry: {
            type: 'LineString',
            coordinates: gpsPointsLngLat,
        },
    };

    // simplify can throw if coordinates are not valid, e.g. too few points for a LineString
    let simplified;

    try {
        simplified = simplify(geojsonLine, { tolerance, highQuality: false });
    } 
    catch(error) {
        console.error("Mapbox Static Image :Error during simplification", error);
        // return original points if simplification fails
        return gpsPointsLatLon; 
    }

    if (!simplified || !simplified.geometry || !simplified.geometry.coordinates) {
        return gpsPointsLatLon;
    }

    // convert back to [lat, lon]
    return simplified.geometry.coordinates.map(p => [p[1], p[0]]);
}

export default class MapboxStaticImage extends React.PureComponent {

    fullscreenElementRef = React.createRef();

    constructor(props) {
        super(props);

        this.state = {
            imageUrl: null, // Current URL for the img tag
            isLoading: true, // True if an image is currently being loaded
            actualImageFailed: false, // True if the primary/actual image load attempt failed
            fallbackImageFailed: false, // True if the fallback image load attempt also failed
        };
    }

    // Add a getter 
    get mapboxBaseUrl() {
        let selectedTheme = lsUtil.getItem('raven.theme');
        if (!selectedTheme) {
            selectedTheme = this.props.defaultTheme || 'light';
        }
        return `https://api.mapbox.com/styles/v1/mapbox/${selectedTheme}-v11/static`;
    }

    get defaultMapboxURL() {
        return `${this.mapboxBaseUrl}/-96.8,47.3,1.8/${this.props.width}x${this.props.height}?access_token=${mapboxToken}`;
    }

    componentDidMount() {

        if (!this.props.isWaitingForGPSData) {
            this.setImageUrl();
        }
    }

    componentDidUpdate(prevProps) {
        const wasWaiting = prevProps.isWaitingForGPSData;
        const isWaiting = this.props.isWaitingForGPSData;


        if ((wasWaiting && !isWaiting)) {
            this.setImageUrl();
        }
    }

    setImageUrl = () => {
        const { hasGPSData, points } = this.props;

        if (!hasGPSData || !Array.isArray(points) || points.length === 0) {
            // No GPS data to generate an "actual" image from path, or no points.
            // Attempt to load the default image and show "no GPS data" message.
            console.warn("MapboxStaticImage: No GPS data or card points, using default image");
            this.setState({
                imageUrl: this.defaultMapboxURL, // Set to default image URL
                isLoading: true,                 // Start loading the default image
                actualImageFailed: true,         // Consider actual image as "failed" or "not applicable"
                fallbackImageFailed: false,        // Fallback (default image) is being attempted
            });
            return;
        }

        // Has GPS data, attempt to load the "actual" image.
        const actualImageUrl = this.generateImageUrl();
        this.setState({
            imageUrl: actualImageUrl,
            isLoading: true,
            actualImageFailed: false,
            fallbackImageFailed: false,
        });
    }
    
    handleImageLoad = () => {
        this.setState({ 
            isLoading: false
            // If actualImageFailed is true here, it means the fallback loaded.
            // If actualImageFailed is false, it means the actual image loaded.
        });
    }

    handleImageError = () => {
        if (!this.state.actualImageFailed) {
            // The "actual" image failed to load.
            console.error("MapboxStaticImage: Error loading actual image. Attempting fallback.");
            this.setState({
                actualImageFailed: true,
                imageUrl: this.defaultMapboxURL, // Switch to fallback URL
                isLoading: true, // Start loading the fallback
                fallbackImageFailed: false, // Reset this as we are now attempting fallback
            });
        } else {
            // The "fallback" image also failed to load (actualImageFailed is already true).
            console.error("MapboxStaticImage: Error loading fallback image. All attempts failed.");
            this.setState({
                // actualImageFailed remains true
                fallbackImageFailed: true,
                isLoading: false, // Stop loading attempts
                imageUrl: null, // No image to display
            });
        }
    }

    requestEventMapBoxImageGPSData = () => {
        // Add null check for this.props.marker
        if (this.props.marker && typeof this.props.marker.latitude === 'number' && typeof this.props.marker.longitude === 'number') {
            const googleMapsURL = `https://www.google.com/maps/search/?api=1&query=${this.props.marker.latitude},${this.props.marker.longitude}`;
            window.open(googleMapsURL, '_blank');
        } else {
            console.warn("MapboxStaticImage: Marker data is not available or invalid to open in Google Maps.");
            // Optionally, provide user feedback here if appropriate
        }
    };

    generateImageUrl = (simplify = false, round = -1) => {

        const { width, height, marker, points } = this.props;

        const reversedPoints = points.slice().reverse();
        let extractedPoints = reversedPoints.map(point => [point.lat, point.lon]);
        
        if(!Array.isArray(extractedPoints) || extractedPoints.length === 0) {
            console.warn("MapboxStaticImage: No GPS data or card points, using default image");
            return this.defaultMapboxURL;
        }

        if (simplify) {
            if (round >= 0 && round < SIMPLIFY_TOLERANCES.length) {
                const tolerance = SIMPLIFY_TOLERANCES[round];
                extractedPoints = simplifyPoints(extractedPoints, tolerance);
            }
        }

        const encodedPath = polyline.encode(extractedPoints);

        const markerParam = marker ? `pin-s-l+${marker.color}(${marker.longitude},${marker.latitude})` : '';
        const pathParam = `path-4+2AAED3-0.8(${encodeURIComponent(encodedPath)})`;
        const overlays = [markerParam, pathParam].filter(Boolean).join(',');
        
        let mapboxUrl = `${this.mapboxBaseUrl}/${overlays}/auto/${width}x${height}?access_token=${mapboxToken}`;

        // Actual limit for Mapbox static image URLs is 8192 characters, but we use 8000 as a safe limit
        if (mapboxUrl.length > 8000) {
            mapboxUrl = this.generateImageUrl(true, round + 1);

        }

        return mapboxUrl;
    }

    render() {

        const { hasGPSData } = this.props;

        const imageControls = (
                <div className="event-media-controls">
                    <div className="pop-out-control">
                        <MapboxControl
                            id="EVENT_MAPBOX_IMAGE_CONTROL_POP_OUT"
                            iconSrc={imageOpenInNew}
                            render={true}
                            pending={this.state.isLoading}
                            onClick={() => { this.requestEventMapBoxImageGPSData() }}
                        />
                    </div>
                </div>
        );

        return (
            <div className="col-12 image-panel panel-wrapper">

                <div className="row section-wrapper event-mapbox-image">
                    <div className="col-12 section-body">
                        <div className="event-media-max-width-480">
                            <div className="event-media-wrapper-16-9-aspect-ratio" ref={this.fullscreenElementRef}>
                                <div className="event-media-wrapper">
                                    <EventMediaPreloaderUnderlay status={this.state.fallbackImageFailed ? "ERROR" : undefined} />

                                    {this.state.imageUrl && !this.state.fallbackImageFailed &&
                                        <img 
                                            key={this.state.imageUrl}
                                            style={{display: this.state.isLoading ? 'none': 'block'}} 
                                            src={this.state.imageUrl}
                                            className="mapbox-static-image"
                                            alt="event location on map"
                                            onLoad={this.handleImageLoad}
                                            onError={this.handleImageError}
                                        />
                                    }

                                    {/* Conditional rendering for messages and controls */}
                                    {!this.state.isLoading && (
                                        <>
                                            {/* Case 1: Fallback image (default map) also failed. Show error, no image. */}
                                            {this.state.fallbackImageFailed && (
                                                <div className="event-media-message-overlay">
                                                    <h3>{strings.mapbox_static_image_error}</h3>
                                                </div>
                                            )}

                                            {/* Case 2: Fallback image is shown (either because actual failed or no GPS data). */}
                                            {!this.state.fallbackImageFailed && this.state.actualImageFailed && (
                                                <>
                                                    {!hasGPSData ? (
                                                        // No GPS data, but fallback (default map) is shown.
                                                        <div className="event-media-message-overlay">
                                                            <h3>{strings.mapbox_static_image_no_gps_data_available}</h3>
                                                        </div>
                                                    ) : (
                                                        // Actual image (with GPS data) failed, fallback (default map) is shown.
                                                        <div className="event-media-message-overlay">
                                                            <h3>{strings.mapbox_static_image_error}</h3>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Case 3: Actual image (specific map with GPS data) is shown. */}
                                            {!this.state.fallbackImageFailed && !this.state.actualImageFailed && hasGPSData && this.state.imageUrl && (
                                                imageControls
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

MapboxStaticImage.propTypes = {
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    marker: PropTypes.shape({
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
        color: PropTypes.string
    }),
    points: PropTypes.array,
    hasGPSData: PropTypes.bool.isRequired,
    defaultTheme: PropTypes.string,
};
